import React from 'react';
import { X, Crown, AlertCircle, FileDown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: 'alerts' | 'export' | 'current-data';
  customMessage?: string;
}

const featureInfo = {
  alerts: {
    icon: AlertCircle,
    title: 'Alert Creation is a Premium Feature',
    description: 'Create custom alerts to track new insider transactions in real-time.',
    benefits: [
      'Unlimited custom alerts',
      'Real-time notifications',
      'Advanced filtering options',
      'Email notifications',
    ],
  },
  export: {
    icon: FileDown,
    title: 'Data Export is a Premium Feature',
    description: 'Export insider trading data and analytics to CSV or Excel for deeper analysis.',
    benefits: [
      'Export to CSV and Excel',
      'Unlimited data exports',
      'Custom export formats',
      'Bulk data downloads',
    ],
  },
  'current-data': {
    icon: TrendingUp,
    title: 'Real-Time Data is a Premium Feature',
    description: 'Access the latest insider trading data without any delays.',
    benefits: [
      'Real-time data updates',
      'No 180-day delay',
      'Latest insider transactions',
      'Current performance metrics',
    ],
  },
};

export function UpgradePromptModal({
  isOpen,
  onClose,
  feature,
  customMessage,
}: UpgradePromptModalProps) {
  if (!isOpen) return null;

  const info = featureInfo[feature];
  const Icon = info.icon;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/30 dark:bg-black/50" onClick={onClose} />

        <div className="relative bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-secondary-400 hover:text-secondary-600 dark:text-secondary-500 dark:hover:text-secondary-300"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-center justify-center w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
            <Crown className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>

          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
            {customMessage || info.title}
          </h3>

          <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-4">
            {info.description}
          </p>

          <div className="bg-secondary-50 dark:bg-secondary-700/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              <p className="text-sm font-medium text-secondary-900 dark:text-white">
                Premium Benefits:
              </p>
            </div>
            <ul className="space-y-2">
              {info.benefits.map((benefit, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-sm text-secondary-600 dark:text-secondary-400"
                >
                  <span className="text-primary-600 dark:text-primary-400 mt-0.5">✓</span>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex gap-3">
            <Link
              to="/settings?tab=subscription"
              className="flex-1 btn btn-primary text-center"
              onClick={onClose}
            >
              Upgrade to Premium
            </Link>
            <button onClick={onClose} className="flex-1 btn btn-secondary">
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
